import useEmailStatusFilters from "@/composables/v2.1/useEmailStatusFilters";
import { PublicApiEndpointPathType, useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useMessagingStatusFilters from "@/composables/v2.1/useMessagingStatusFilters";
import axios from "@/libs/axios";
import { StatusFilterNameType } from "@/types/filter.types";
import { InboxModuleType } from "@/types/inboxModule.type";
import { ReadDto } from "@saysimple-platform/dtos";
import { computed, reactive, toRefs } from "@vue/composition-api";
import { AxiosResponse } from "axios";
import { clone } from "lodash";

interface RootState {
    [InboxModuleType.MESSAGING]: {
        unreadChats: ReadDto[];
    };
    [InboxModuleType.EMAIL]: {
        unreadChats: ReadDto[];
    };
    [InboxModuleType.TEAM]: {
        unreadChats: ReadDto[];
    };
}

const state: RootState = reactive<RootState>({
    [InboxModuleType.MESSAGING]: {
        unreadChats: [],
    },
    [InboxModuleType.EMAIL]: {
        unreadChats: [],
    },
    [InboxModuleType.TEAM]: {
        unreadChats: [],
    },
});

const { inboxModuleType } = useInboxModuleType();

export default function useUnreadChats(inboxModule: InboxModuleType = inboxModuleType.value) {
    const { filterUnreadChats } = inboxModule === "email" ? useEmailStatusFilters() : useMessagingStatusFilters();
    const { isInboxModuleEnabled } = useInboxModuleType();

    const fetchUnreadChats = () =>
        Promise.all([fetchUnreadMessagingChats(), fetchUnreadEmailChats(), fetchUnreadTeamsChats()]);

    const fetchUnreadMessagingChats = (): Promise<ReadDto[]> => {
        if (!isInboxModuleEnabled(InboxModuleType.MESSAGING)) {
            return Promise.reject();
        }

        return axios
            .get(`/${PublicApiEndpointPathType.MESSAGING}/count`)
            .then((messagingResults: AxiosResponse<Record<StatusFilterNameType, ReadDto[]>>) => {
                const messagingUnreadChats: ReadDto[] = [];

                Object.values<ReadDto[]>(messagingResults.data).forEach((unreadChats) => {
                    messagingUnreadChats.push(
                        ...unreadChats
                            .filter((chat) =>
                                messagingUnreadChats.every((existingChat) => chat.chatId !== existingChat.chatId)
                            )
                            .map(
                                (chat: ReadDto) =>
                                    ({
                                        ...chat,
                                        chatId: chat.chatId,
                                        channelId: chat.channelId,
                                        //tagIds: chat.tags?.map((tag) => tag.tagId) ?? [],
                                        tagIds: chat.tagIds,
                                        assignedTeamId: chat.assignedTeamId,
                                        assignedUserId: chat.assignedUserId,
                                        userId: chat.userId,
                                        isChatRead: chat.isChatRead,
                                        lastEntryReadAt: chat.lastEntryReadAt,
                                        status: "open",
                                    } as const)
                            )
                    );
                });
                state.messaging.unreadChats = messagingUnreadChats;

                return messagingUnreadChats;
            });
    };

    const fetchUnreadEmailChats = (): Promise<ReadDto[]> => {
        if (!isInboxModuleEnabled(InboxModuleType.EMAIL)) {
            return Promise.reject();
        }

        return axios.get(`/${PublicApiEndpointPathType.EMAIL}/count`).then(
            (
                emailResults: AxiosResponse<{
                    statusFilter: Record<StatusFilterNameType, ReadDto[]>;
                }>
            ) => {
                const emailUnreadChats: ReadDto[] = [];

                Object.values<ReadDto[]>(emailResults.data.statusFilter).forEach((unreadChats) => {
                    emailUnreadChats.push(
                        ...unreadChats
                            .filter((chat) =>
                                emailUnreadChats.every((existingChat) => chat.chatId !== existingChat.chatId)
                            )
                            .map(
                                (chat: ReadDto) =>
                                    ({
                                        ...chat,
                                        chatId: chat.chatId,
                                        channelId: chat.channelId,
                                        //tagIds: chat.tags?.map((tag) => tag.tagId) ?? [],
                                        tagIds: chat.tagIds,
                                        assignedTeamId: chat.assignedTeamId,
                                        assignedUserId: chat.assignedUserId,
                                        userId: 1,
                                        readForAll: true,
                                        isChatRead: false,
                                        lastEntryReadAt: null,
                                        status: "open",
                                    } as const)
                            )
                    );
                });
                state.email.unreadChats = emailUnreadChats;

                return emailUnreadChats;
            }
        );
    };

    const fetchUnreadTeamsChats = (): Promise<ReadDto[]> => {
        if (!isInboxModuleEnabled(InboxModuleType.TEAM)) {
            return Promise.reject();
        }

        return axios.get(`/${PublicApiEndpointPathType.TEAM}/count`).then((teamResults) => {
            let teamUnreadChats = teamResults.data.filter((chat) => chat.unreadMessages > 0 && !chat.deletedAt);
            teamUnreadChats = teamUnreadChats.map((chat) => {
                return { ...chat, chatId: chat.id };
            });
            state.team.unreadChats = teamUnreadChats;
            return teamUnreadChats;
        });
    };

    const totalFilteredUnreadMessagingChatsCount = computed((): number => {
        return filterUnreadChats(InboxModuleType.MESSAGING).length;
    });

    const totalFilteredUnreadEmailChatsCount = computed((): number => {
        return filterUnreadChats(InboxModuleType.EMAIL).length;
    });

    const totalFilteredUnreadTeamChatsCount = computed((): number => {
        // we don't need to use filterUnreadChats because the team chat data don't have the same geometry
        // and we do the filter of them after the request
        return state.team.unreadChats.length;
    });

    const totalFilteredUnreadChatsCount = computed((): number => {
        return Number(
            totalFilteredUnreadMessagingChatsCount.value +
                totalFilteredUnreadEmailChatsCount.value +
                totalFilteredUnreadTeamChatsCount.value
        );
    });

    const deleteUnreadChat = (chatId: number): void => {
        const unreadChatsIndex = state[inboxModule].unreadChats.findIndex(
            (findChat: ReadDto) => findChat.chatId === chatId
        );

        if (unreadChatsIndex === -1) {
            return;
        }

        state[inboxModule].unreadChats.splice(unreadChatsIndex, 1);
    };

    const addUnreadChat = (chat: ReadDto): void => {
        if (!chat.chatId) {
            throw new Error("No chatId passed.");
        }
        // If we find a chat by id that is already in the unread list, then ignore adding them again.
        const unreadChatsIndex = state[inboxModule].unreadChats.findIndex(
            (findChat: ReadDto) => findChat.chatId === chat.chatId
        );

        if (unreadChatsIndex !== -1) {
            return;
        }

        if (inboxModuleType.value === InboxModuleType.EMAIL) {
            chat.status = "open";
        }

        state[inboxModule].unreadChats.push(chat);
    };

    const updateUnreadChat = (chat: Partial<ReadDto> & Pick<ReadDto, "chatId" | "status">) => {
        if (!chat.chatId) {
            throw new Error("No chatId passed.");
        }

        const unreadChatsIndex = state[inboxModule].unreadChats.findIndex(
            (unreadChat: ReadDto) => unreadChat.chatId === chat.chatId
        );

        let original: ReadDto;
        if (typeof chat.isChatRead === "undefined") {
            original = clone(state[inboxModule].unreadChats[unreadChatsIndex]);
        }

        if (unreadChatsIndex !== -1) {
            deleteUnreadChat(chat.chatId);
        }

        if (chat.status === "open" && (original || chat.isChatRead === false)) {
            addUnreadChat({
                ...original,
                ...chat,
            });
        }
    };

    return {
        totalFilteredUnreadMessagingChatsCount,
        totalFilteredUnreadEmailChatsCount,
        totalFilteredUnreadChatsCount,
        totalFilteredUnreadTeamChatsCount,
        addUnreadChat,
        deleteUnreadChat,
        fetchUnreadChats,
        updateUnreadChat,
        ...toRefs(state),
        state,
    };
}
